import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { Snackbar } from 'components/Snackbar'
import { pageCardClasses } from 'components/AppLayout/styles'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { commonStrings, tagManagementStrings } from 'constants/strings'
import { useDeleteTag, useTags } from 'hooks'
import type { TTag } from 'types'
import { TagsCardList } from './TagsCardList'
import { TagsTable } from './TagsTable'

type TagManagementLocationState = {
  snackbarMessage?: string
}

export default function TagManagement() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const location = useLocation()
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [tagToDelete, setTagToDelete] = useState<TTag | null>(null)

  const { tags, loading, error } = useTags()
  const {
    remove,
    isDeleting,
    error: deleteError,
  } = useDeleteTag({
    onSuccess: () => {
      setTagToDelete(null)
      setSnackbarMessage(tagManagementStrings.messages.deleteSuccess)
      setIsSnackbarOpen(true)
    },
  })

  useEffect(() => {
    const state = location.state as TagManagementLocationState | null
    const message = state?.snackbarMessage
    if (!message) return

    setSnackbarMessage(message)
    setIsSnackbarOpen(true)
    navigate(orgPathFor(routes.TAG_MANAGEMENT), { replace: true, state: null })
  }, [location.state, navigate, orgPathFor])

  const countLabel = `${tags.length}${commonStrings.countSuffix}`
  const viewError = error || deleteError

  const handleEdit = (tagId: number) =>
    navigate(orgPathFor(routes.TAG_MANAGEMENT_EDIT.replace(':tagId', String(tagId))))

  const handleConfirmDelete = () => {
    if (tagToDelete) {
      remove(tagToDelete.id)
    }
  }

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {tagManagementStrings.title}
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <Button
              type="button"
              size="sm"
              onClick={() => navigate(orgPathFor(routes.TAG_MANAGEMENT_CREATE))}
            >
              {tagManagementStrings.actions.createTag}
            </Button>
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
          </div>
        </header>

        {viewError && (
          <div className="mt-6">
            <Alert>{viewError}</Alert>
          </div>
        )}

        <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
          <TagsTable
            tags={tags}
            loading={loading}
            onEdit={handleEdit}
            onDelete={setTagToDelete}
          />
        </div>
        <div className="mt-6 lg:hidden">
          <TagsCardList
            tags={tags}
            loading={loading}
            onEdit={handleEdit}
            onDelete={setTagToDelete}
            className="md:grid-cols-2"
          />
        </div>
      </section>

      <ConfirmModal
        open={tagToDelete !== null}
        textTitle={tagManagementStrings.delete.title}
        textMessage={tagManagementStrings.delete.message}
        textCancel={commonStrings.cancel}
        textConfirm={tagManagementStrings.delete.confirm}
        isLoading={isDeleting}
        onClose={() => setTagToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        variant="success"
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </>
  )
}
